import { formatAddress, formatAmount } from "../../utils";
import { ArrowDownIcon, MoneyBoxIcon, PiggyBankIcon, RecycleIcon, WalletIcon } from "../SVGIcons";
import { MoneyIcon } from "../SVGIcons";
import { useTheme } from "../../context/ThemeContext";
import CopyBtn from "../CopyBtn";
import { useEVMContext } from "../../context/EVMContext";

const ItemCard = ({ title, value, iconColor, textColor, cardView }: { title: string, value: React.ReactNode, iconColor: string, textColor: string, cardView: React.ReactNode }) => {
    return (
        <div
            className={`flex items-center gap-[24px]  ${textColor}`}
        >
            <div className={`${iconColor} w-[40px] h-[40px] flex items-center justify-center rounded-full`}>
                {cardView}
            </div>
            <div className="flex flex-col gap-[4px] leading-[1.2]">
                <span className="text-[14px] sm:text-[14px] text-black/70 dark:text-white/70">{title}</span>
                <div className="font-medium"> {value}</div>
            </div>
        </div>
    );
};

const SkeletonItemCard = ({ title, iconColor, cardView }: { title: string, iconColor: string, cardView: React.ReactNode }) => {
    return (
        <div className={`flex items-center gap-[24px] text-black dark:text-white`}>
            <div className={`${iconColor} w-[40px] h-[40px] flex items-center justify-center rounded-full`}>
                {cardView}
            </div>
            <div className="flex flex-col gap-[4px] leading-[1.2]">
                <span className="text-[14px] sm:text-[14px] text-black/70 dark:text-white/70">{title}</span>
                <div className="font-medium">
                    <div className="h-4 bg-black/10 dark:bg-white/10 rounded animate-pulse w-20"></div>
                </div>
            </div>
        </div>
    );
};

const SubnetUserInfo = ({ traderData, balance }: { traderData: any, balance: string }) => {
    const { isDark } = useTheme();
    const { accounts } = useEVMContext();

    if (!traderData) {
        return (
            <div className="rounded-lg pb-[16px]">
                <div className="flex flex-col gap-[24px]">
                    <SkeletonItemCard title="Address" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<WalletIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Balance" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<MoneyIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Collateral" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<ArrowDownIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Borrow Available" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<MoneyBoxIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Staked" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<PiggyBankIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Current Stakes" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<PiggyBankIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Borrowed Staked Amount" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<MoneyBoxIcon fillColor={isDark ? '#fff' : '#000'} />} />
                    <SkeletonItemCard title="Pending Borrow Available" iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"} cardView={<RecycleIcon fillColor={isDark ? '#fff' : '#000'} />} />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg pb-[16px]">
            <div className="flex flex-col gap-[24px]">
                <ItemCard
                    cardView={<WalletIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{formatAddress(accounts[0])}</span>
                            <div className="ml-[10px] w-[20px] h-[20px] flex items-center justify-center">
                                <CopyBtn content={accounts[0] || ''} />
                            </div>
                        </div>
                    )}
                    title="Address"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<MoneyIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{balance ? 'τ' + formatAmount((Number(balance.replace(/,/g, ''))).toString()) : '0'}</span>
                        </div>
                    )}
                    title="Balance"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<ArrowDownIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center" >
                            <span>{`τ${formatAmount(traderData?.collateral_amount?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Collateral"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<MoneyBoxIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{`τ${formatAmount(traderData?.available_amount?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Borrow Available"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<PiggyBankIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{`τ${formatAmount(traderData?.active_stakes_sum?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Staked"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<PiggyBankIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{`τ${formatAmount(traderData?.current_stakes_amount?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Current Stakes"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<MoneyBoxIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{`τ${formatAmount(traderData?.borrowed_stakes_amount?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Borrowed Staked Amount"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
                <ItemCard
                    cardView={<RecycleIcon fillColor={isDark ? '#fff' : '#000'} />}
                    value={(
                        <div className="flex items-center">
                            <span>{`τ${Math.abs(Number(traderData?.available_pending_amount)) < 1e-10 ? '0.000' : formatAmount(traderData?.available_pending_amount?.toString() || "0")}`}</span>
                        </div>
                    )}
                    title="Pending Borrow Available"
                    iconColor={isDark ? "bg-[#262626]" : "bg-[#F2F2F2]"}
                    textColor={isDark ? "text-white" : "text-black"}
                />
            </div>
        </div>
    );
};

export default SubnetUserInfo;